// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { createWithResources, loginWithResources } from 'client/actions/user';
import CreateForm from 'client/components/account/createForm';
import BrandLogo from 'client/components/brandLogo';
import Notification from 'client/components/notification';
import ScrollTo from 'client/components/scrollTo';
import Translate from 'client/components/translate';
import 'client/styles/account.scss';

import { addressType } from 'types/prop-types';
import type { IfpAppType } from 'web-core/types/ifp-app';
import type { MemberType } from 'web-core/types/member';

const CREATE_FORM_TYPE = 'create';
const LOGIN_FORM_TYPE = 'login';
const TRACKING_SOURCE = 'vision-buy';

const mapStateToProps: Object = state => ({
  address: state.address,
  apps: state.ifpApps,
  brandName: state.brand,
  members: state.members,
  partnerName: state.partnerName,
  user: state.user,
});

type Props = {
  brandName: string,
  loginWithResources: Function,
  createWithResources: Function,
  address: addressType,
  members: Array<MemberType>,
  apps: Array<IfpAppType>,
  partnerName: string | null,
};

export class AccountPage extends Component<Props> {
  static defaultProps = {
    address: {},
    members: [],
    apps: [],
    partnerName: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      formType: CREATE_FORM_TYPE,
    };

    // Hide solvvy widget for non-logged in user when redirected here
    const solvvyWidget = document.getElementById('solvvy-support-flow-launch-widget');

    if (solvvyWidget) {
      solvvyWidget.style = 'display: none;';
    }
  }

  onLogin = ({ email, password }) => {
    const { address, members, apps, partnerName } = this.props;
    this.setState({
      submitFailed: false,
      submitFailedMessage: null,
    });
    return this.props
      .loginWithResources({
        email,
        password,
        source: TRACKING_SOURCE,
        partnerName,
        apps,
        members,
        address,
      })
      .then(() => {
        window.location.replace('/purchase');
      })
      .catch(() => {
        this.setState({
          submitFailed: true,
          submitFailedMessage: 'common.error.loginFail',
        });
      });
  };

  onCreate = ({ email, password }) => {
    const { address, members, apps, partnerName } = this.props;
    return this.props
      .createWithResources({
        address,
        apps,
        email,
        members,
        partnerName,
        password,
        source: TRACKING_SOURCE,
      })
      .then(() => {
        window.location.replace('/purchase');
      })
      .catch((err) => {
        const errMsg = _.get(err, 'response.data.message', '');
        const errState = {
          formType: LOGIN_FORM_TYPE,
          submitFailed: true,
        };
        if (errMsg.indexOf('exists') >= 0) {
          errState.submitFailedMessage = 'common.error.existingAccount';
        }
        this.setState(errState);
      });
  };

  onSubmitFail = () => {
    this.setState({
      submitFailed: true,
      scrollTop: this.needsScroll(),
    });
  };

  needsScroll() {
    const scrollingElement = document.scrollingElement || document.documentElement;
    return scrollingElement.scrollTop !== 0;
  }

  toggleForm = (e) => {
    e.preventDefault();
    const formType = this.state.formType === CREATE_FORM_TYPE ? LOGIN_FORM_TYPE : CREATE_FORM_TYPE;

    this.setState({
      formType,
      submitFailed: false,
      submitFailedMessage: null,
      scrollTop: this.needsScroll(),
    });
  };

  render() {
    const { formType } = this.state;
    const isCreate = formType === CREATE_FORM_TYPE;
    const commonPrefix = 'common.account';
    const formTypePrefix = `${commonPrefix}.${formType}`;
    const ctaPath = `${formTypePrefix}.cta`;
    const submitHandler = isCreate ? this.onCreate : this.onLogin;

    return (
      <div className="account-page">
        <div className="hero flex-row">
          <div className="center-col-small flex-column">
            <BrandLogo assetBase="/assets" brandName={this.props.brandName} />
          </div>
        </div>

        {this.state.submitFailed && (
          <Notification theme="red" border borderPosition="bottom">
            <div className="text-center" style={{ maxWidth: '36rem', margin: 'auto' }}>
              <h5 className="red-color">
                <Translate path="common.error.genericHeader" />
              </h5>
              <h5 className="grey-2-color light">
                <Translate path={this.state.submitFailedMessage || 'common.error.genericMessage'} />
              </h5>
            </div>
          </Notification>
        )}

        {this.state.scrollTop && (
          <ScrollTo
            to={0}
            onRest={() => {
              this.setState({
                scrollTop: false,
              });
            }} />
        )}

        <div className="center-col-small" style={{ paddingBottom: '4rem' }}>
          <div>
            <h4 className="pad-top-2 pad-x-small-only">
              <Translate path={`${formTypePrefix}.hero.plan`} />
            </h4>
            <CreateForm
              ctaPath={ctaPath}
              submitHandler={submitHandler}
              type={formType}
              submitFailHandler={this.onSubmitFail} />
            <div className="text-center grey-3-color">
              <a
                className="ib margin-top-2"
                style={{
                  width: '100%',
                }}
                href="/"
                onClick={this.toggleForm}>
                <div>
                  <strong>
                    <Translate path={`${formTypePrefix}.changeCta`} />
                  </strong>
                </div>
              </a>

              {isCreate ? (
                <p className="margin-2">
                  <Translate path={`${formTypePrefix}.legal`} />
                </p>
              ) : (
                <div className="margin-top-2">
                  <Link to="/onboarding/account/reset">
                    <u>
                      <Translate path={`${formTypePrefix}.resetPassword`} />
                    </u>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, {
  createWithResources,
  loginWithResources,
})(AccountPage);
